import { useState, useMemo, useEffect } from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash.debounce";
import useGet from "../../hooks/api/useGet";

export default function ApiAutoComplete({
  apiUrl,
  value = null,
  onChange,
  label = "Select Option",
  creatable = false,
  defaultParams = {},
  TextFieldProps = {},
  ...props
}) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Create a debounced version of setDebouncedSearch
  const debouncedSetSearch = useMemo(
    () =>
      debounce((search) => {
        setDebouncedSearch(search);
      }, 300),
    []
  );

  // Update debounced search when input value changes and dropdown is open
  useEffect(() => {
    if (isOpen) {
      debouncedSetSearch(inputValue);
    }
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [inputValue, debouncedSetSearch, isOpen]);

  // Fetch options using useGet hook
  const { data, isLoading: loading } = useGet(apiUrl, {
    params: {
      search_input: debouncedSearch,
      ...defaultParams,
    },
    enabled: isOpen,
    staleTime: 1000 * 60 * 2, // Cache for 2 minutes
  });

  const options = data?.data || [];

  // Reset input value when value is cleared and dropdown is closed
  useEffect(() => {
    if (value == null && !isOpen) {
      setInputValue("");
    }
  }, [value, isOpen]);

  // Only create the filter if the creatable prop is enabled
  const filter = creatable ? createFilterOptions() : null;

  return (
    <Autocomplete
      value={value ? value : props?.multiple ? [] : null}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
      onChange={(event, newValue) => {
        if (creatable) {
          if (typeof newValue === "string") {
            onChange &&
              onChange({ value: newValue, label: newValue, created: true });
          } else if (newValue && newValue.inputValue) {
            onChange &&
              onChange({
                value: newValue.inputValue,
                label: newValue.inputValue,
                created: true,
              });
          } else {
            onChange && onChange(newValue);
          }
        } else {
          onChange && onChange(newValue);
        }
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        // Only update inputValue if the change is from user input
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return (
          option.value === (typeof value === "string" ? value : value.value)
        );
      }}
      options={options}
      loading={loading}
      noOptionsText="No options"
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        return option.label;
      }}
      filterOptions={
        creatable && filter
          ? (options, params) => {
              const filtered = filter(options, params);
              const { inputValue } = params;
              const isExisting = options.some(
                (option) => inputValue === option.label
              );
              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  inputValue,
                  label: inputValue,
                  value: inputValue,
                  created: true,
                });
              }
              return filtered;
            }
          : (x) => x
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={props?.variant ?? "standard"}
          {...TextFieldProps}
          slotProps={{
            input: {
              ...TextFieldProps.InputProps,
              ...params.InputProps,
              endAdornment: loading ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                params.InputProps.endAdornment
              ),
            },
          }}
        />
      )}
      {...props}
    />
  );
}
